import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';
import { Company } from 'src/interfaces/company';
import {
  IndivdiualCreatePayload,
  IndividualEditPayload,
  Individual,
} from 'src/interfaces/individual';
import { IndividualSearchDialogComponent } from '../components/individual-search-dialog/individual-search-dialog.component';
import { AlertService } from './alert.service';
import { AuthService } from './auth.service';
import { CompanyService } from './company.service';
import { ConfirmationService } from './confirmation.service';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class IndividualService {
  baseUrl: string = environment.api.baseUrl;
  headers: HttpHeaders;
  totalNumberOfIndividuals: number = 0;

  constructor(
    private _http: HttpClient,
    private _alertService: AlertService,
    private _auth: AuthService,
    private _companyService: CompanyService,
    private _confirmationService: ConfirmationService,
    private _utilsService: UtilsService,
    private _dialog: MatDialog
  ) {
    this._auth.generateRequestHeaders().then((headers) => {
      this.headers = headers;
    });
  }

  async createIndividual(
    payload: IndivdiualCreatePayload
  ): Promise<Individual | null> {
    const confirm = await this._confirmationService.confirmSave();
    if (!confirm) return null;

    try {
      const { data, message } = await this._http
        .post<any>(`${this.baseUrl}/individual`, payload, {
          headers: this.headers,
        })
        .toPromise();

      this._alertService.openSnackBar(message);

      return data ? this.normalizeIndividual(data) : null;
    } catch (error) {
      this._utilsService.showError(error);

      return null;
    }
  }

  async editIndividual(
    payload: IndividualEditPayload,
    id: number
  ): Promise<boolean> {
    const confirm = await this._confirmationService.confirmSave();

    if (!confirm) return false;

    try {
      const { success, message } = await this._http
        .put<any>(`${this.baseUrl}/individual/${id}`, payload, {
          headers: this.headers,
        })
        .toPromise();

      this._alertService.openSnackBar(message);

      return success;
    } catch (error) {
      this._utilsService.showError(error);
      return false;
    }
  }

  /** Map API snake/camel variants so Sage status stays consistent after GET (same pattern as CompanyService). */
  normalizeIndividual(raw: any): Individual {
    const sid =
      raw.sage_contact_id ?? raw.sageContactId ?? raw.sage_contactId ?? null;
    const distributor_id =
      raw.distributor_id ?? raw.distributorId ?? null;
    const distributor = raw.distributor ?? null;
    return {
      ...raw,
      sage_contact_id:
        sid != null && String(sid).trim() !== ''
          ? String(sid)
          : raw.sage_contact_id ?? null,
      distributor_id,
      distributor: distributor
        ? this._companyService.normalizeCompany(distributor)
        : null,
    } as Individual;
  }

  async getIndividual(id: number): Promise<Individual | null> {
    try {
      const { data } = await this._http
        .get<any>(`${this.baseUrl}/individual/${id}`, {
          headers: this.headers,
        })
        .toPromise();

      if (data == null) return null;
      return this.normalizeIndividual(data);
    } catch (error) {
      this._utilsService.showError(error);
      return null;
    }
  }

  async deleteIndividual(id: number): Promise<boolean> {
    const confirm: boolean = await this._confirmationService.confirm(
      'Delete individual',
      'Are you sure you want to delete this individual?'
    );

    if (!confirm) return false;

    try {
      const { success, message } = await this._http
        .delete<any>(`${this.baseUrl}/individual/${id}`, {
          headers: this.headers,
        })
        .toPromise();

      this._alertService.openSnackBar(message);

      return success;
    } catch (error) {
      this._utilsService.showError(error);
      return false;
    }
  }

  async getIndividuals(
    page: number,
    limit: number,
    types: string | null = null,
    search: string | null = null,
    companyId: number | null = null,
    sort: string = 'asc',
    orderBy: string = 'first_name'
    ): Promise<Individual[]> {

    let queryString: string = `page=${page}&limit=${limit}`;

    if (search) queryString += `&search=${search}`;

    if (types) queryString += `&types=${types}`;

    if (companyId) queryString += `&company_id=${companyId}`;
    
    if (sort) queryString += `&order_direction=${sort}`;
    
    if (orderBy) queryString += `&order_by=${orderBy}`;
    

    try {
      const { data, message } = await this._http
        .get<any>(`${this.baseUrl}/individual?${queryString}`, {
          headers: this.headers,
        })
        .toPromise();
      this.totalNumberOfIndividuals = data.total;
      return (data.results ?? []).map((row: any) => this.normalizeIndividual(row));
    } catch (error) {
      this._utilsService.showError(error);
      return [];
    }
  }

  getTotalNumberOfIndividuals(): number {
    return this.totalNumberOfIndividuals;
  }

  async getCompanies(
    page: number,
    limit: number,
    search: string
  ): Promise<Company[]> {
    try {
      const response = await this._http
        .get<any>(
          `${this.baseUrl}/company?page=${page}&limit=${limit}&search=${search}`,
          {
            headers: this.headers,
          }
        )
        .toPromise();
      return response.data.results;
    } catch (error) {
      this._utilsService.showError(error);
      return [];
    }
  }

  async getCompany(id: number): Promise<Company | null> {
    try {
      const { data } = await this._http
        .get<any>(`${this.baseUrl}/company/${id}`, {
          headers: this.headers,
        })
        .toPromise();
      if (data == null) return null;
      return this._companyService.normalizeCompany(data);
    } catch (error) {
      this._utilsService.showError(error);
      return null;
    }
  }

  openIndividualSearchDialog(
    title: string = 'Search for an individual',
    message: string = '',
    types: string | null = null,
    company: Company | null = null
  ): Promise<Individual | null> {
    const dialogRef = this._dialog.open(IndividualSearchDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      panelClass: 'mbp-confirm-dialog',
      data: {
        title,
        message,
        types,
        company,
      },
    });

    return dialogRef.afterClosed().toPromise();
  }

  constructIndividualEditPayload(
    individual: Individual
  ): IndividualEditPayload {
    return {
      id: individual.id,
      profile_number: individual.profile_number,
      first_name: individual.first_name,
      last_name: individual.last_name,
      initials: individual.initials,
      gender: individual.gender,
      title: individual.title,
      dependant_number: individual.dependant_number,
      date_of_birth: individual.date_of_birth,
      is_animal: Boolean(individual.is_animal),
      phone_number: individual.phone_number,
      phone_number_alt: individual.phone_number_alt,
      email: individual.email,
      email_alt: individual.email_alt,
      individual_type_id: individual.individual_type_id
        ? individual.individual_type_id
        : undefined,
      company_id: individual.company_id ? individual.company_id : undefined,
      distributor_id: individual.distributor_id
        ? individual.distributor_id
        : undefined,
      address_id: individual.address_id ? individual.address_id : undefined,
      address: undefined,
    };
  }
}
